import type { SizeUnit } from "@/lib/listing/fieldConfigs";

/** Compact capacity tokens in titles: 2HP, 200L, 5KVA (no space before unit). */
export function normalizeCompactCapacityInText(text: string): string {
  let t = text.trim();
  t = t.replace(/(\d+(?:\.\d+)?)\s+HP\b/gi, "$1HP");
  t = t.replace(/(\d+(?:\.\d+)?)\s+KVA\b/gi, "$1KVA");
  t = t.replace(/(\d[\d,]*)\s+L\b/gi, (_, digits: string) => {
    return `${digits.replace(/,/g, "")}L`;
  });
  t = t.replace(/(\d+(?:\.\d+)?)\s+kg\b/gi, "$1kg");
  return t;
}

/** Short unit shown in the size field badge and stored on the listing (titles, DB). */
export function abbreviatedSizeUnitLabel(unit: SizeUnit): string {
  switch (unit) {
    case "liters":
      return "L";
    case "inches":
      return "In";
    case "ft":
      return "Ft";
    case "KVA":
      return "KVA";
    case "kg":
      return "kg";
    case "HP":
      return "HP";
    case "varies":
      return "";
  }
}

function storageSuffix(unit: Exclude<SizeUnit, "varies">): string {
  switch (unit) {
    case "liters":
      return "L";
    case "inches":
      return " In";
    case "ft":
      return " Ft";
    case "KVA":
      return "KVA";
    case "kg":
      return "kg";
    case "HP":
      return "HP";
  }
}

function hasExplicitSizeUnit(
  trimmed: string,
  unit: Exclude<SizeUnit, "varies">,
): boolean {
  const t = trimmed;
  switch (unit) {
    case "liters":
      return /liters?|\d\s*L\b|[\d,]+L\b/i.test(t);
    case "inches":
      return /inches?|inch|"|\bin\b|\bIn\b/i.test(t);
    case "ft":
      return /\b(ft|feet|foot)\b|\bFt\b|[0-9]\s*ft\b/i.test(t);
    case "KVA":
      return /kva/i.test(t);
    case "kg":
      return /\bkg\b/i.test(t);
    case "HP":
      return /\d\s*HP\b|\bhp\b|horse\s*power/i.test(t);
  }
}

function stripConfiguredUnitCore(
  trimmed: string,
  unit: Exclude<SizeUnit, "varies">,
): string {
  let t = trimmed;
  switch (unit) {
    case "liters":
      t = t.replace(/\s+(liters?)\s*$/i, "");
      t = t.replace(/\s+L\s*$/i, "");
      t = t.replace(/([0-9,]+(?:\.[0-9]+)?)L$/i, "$1");
      return t.trim();
    case "inches":
      t = t.replace(/\s+(inches?|inch)\s*$/i, "");
      t = t.replace(/\s+in\s*$/i, "");
      t = t.replace(/\s+In\s*$/i, "");
      t = t.replace(/"+\s*$/, "");
      return t.trim();
    case "ft":
      t = t.replace(/\s+(ft|feet|foot)\s*$/i, "");
      t = t.replace(/\s+Ft\s*$/i, "");
      return t.trim();
    case "KVA":
      t = t.replace(/\s*kva\s*$/i, "");
      t = t.replace(/(\d(?:\.\d+)?)KVA$/i, "$1");
      return t.trim();
    case "kg":
      t = t.replace(/\s*kg\s*$/i, "");
      return t.trim();
    case "HP":
      t = t.replace(/\s*horse\s*power\s*$/i, "");
      t = t.replace(/\s*hp\s*$/i, "");
      t = t.replace(/(\d(?:\.\d+)?)HP$/i, "$1");
      return t.trim();
  }
}

/** Value shown in the text box when the category uses a fixed unit (strip trailing unit from stored value). */
export function displaySizeTextWithoutConfiguredUnit(
  value: string,
  unit: SizeUnit,
): string {
  if (unit === "varies") return value;
  const trimmed = value.trim();
  if (!trimmed) return "";
  return stripConfiguredUnitCore(trimmed, unit);
}

/**
 * Normalizes free-text size for parent state: appends abbreviated unit when missing
 * so titles and listing details stay compact.
 */
export function finalizeSizeTextWithUnit(raw: string, unit: SizeUnit): string {
  if (unit === "varies") return normalizeCompactCapacityInText(raw);
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const u = unit as Exclude<SizeUnit, "varies">;
  const withUnit = hasExplicitSizeUnit(trimmed, u)
    ? trimmed
    : u === "liters"
      ? `${trimmed.replace(/,/g, "")}L`
      : `${trimmed}${storageSuffix(u)}`;
  return normalizeCompactCapacityInText(withUnit);
}
